import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatMAD, resolveMediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useI18n } from "@/context/I18nContext";
import { Check, Truck, ShieldCheck, RotateCcw, ArrowLeft, Heart } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function ProductPage() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [size, setSize] = useState(null);
    const [selectedImg, setSelectedImg] = useState(0);
    const [loading, setLoading] = useState(true);
    const { addItem } = useCart();
    const { has, toggle } = useWishlist();
    const { t } = useI18n();

    useEffect(() => {
        setLoading(true);
        setSize(null);
        setSelectedImg(0);
        api.get(`/products/${id}`)
            .then((r) => setProduct(r.data))
            .catch(() => setProduct(null))
            .finally(() => setLoading(false));
    }, [id]);

    const handleAdd = () => {
        if (!product) return;
        if (product.sizes?.length > 0 && !size) {
            toast.error(t("product.select_size"));
            return;
        }
        addItem(product, size);
        toast.success(t("product.added"), {
            description: `${product.name}${size ? ` · ${size}` : ""}`,
        });
    };

    if (loading) {
        return (
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-10">
                <div className="aspect-[4/5] bg-white/5 animate-pulse" />
                <div className="space-y-4">
                    <div className="h-8 bg-white/5 animate-pulse" />
                    <div className="h-4 w-32 bg-white/5 animate-pulse" />
                    <div className="h-24 bg-white/5 animate-pulse" />
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-32 text-center">
                <p className="font-display text-3xl uppercase font-black">{t("product.not_found")}</p>
                <Link
                    to="/collection"
                    className="inline-flex mt-6 items-center gap-2 text-xs uppercase tracking-[0.2em] link-underline"
                >
                    <ArrowLeft size={14} className="rtl:rotate-180" /> {t("product.back_collection")}
                </Link>
            </div>
        );
    }

    const isFav = has(product.id);

    return (
        <div data-testid="product-page" className="mx-auto max-w-[1400px] px-5 sm:px-8 py-8 sm:py-12">
            <Seo
                title={product.name}
                description={product.description}
                image={resolveMediaUrl(product.images?.[0])}
                type="product"
                price={product.price}
            />

            <Link
                to="/collection"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white mb-8 link-underline"
                data-testid="product-back-link"
            >
                <ArrowLeft size={14} className="rtl:rotate-180" /> {t("nav.collection")}
            </Link>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
                <div>
                    <div className="aspect-[4/5] bg-[#0f0f0f] overflow-hidden">
                        <img
                            src={resolveMediaUrl(product.images[selectedImg])}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            data-testid="product-main-image"
                        />
                    </div>
                    {product.images.length > 1 && (
                        <div className="mt-3 grid grid-cols-4 gap-3">
                            {product.images.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => setSelectedImg(i)}
                                    className={`aspect-square bg-[#0f0f0f] overflow-hidden border ${
                                        selectedImg === i
                                            ? "border-white"
                                            : "border-transparent hover:border-white/30"
                                    }`}
                                    data-testid={`product-thumb-${i}`}
                                >
                                    <img
                                        src={resolveMediaUrl(img)}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="md:sticky md:top-24 md:self-start">
                    <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">
                        {product.category === "tshirt" ? t("product.tshirt") : t("product.cap")}
                    </p>
                    <div className="flex items-start justify-between gap-4">
                        <h1
                            className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tighter leading-none"
                            data-testid="product-title"
                        >
                            {product.name}
                        </h1>
                        <button
                            onClick={() => toggle(product.id)}
                            className={`flex-shrink-0 w-11 h-11 border flex items-center justify-center transition-colors ${
                                isFav
                                    ? "bg-white text-black border-white"
                                    : "border-white/30 hover:border-white"
                            }`}
                            aria-label={isFav ? t("wishlist.remove") : t("wishlist.add")}
                            data-testid="product-wishlist-btn"
                        >
                            <Heart size={18} strokeWidth={1.5} fill={isFav ? "currentColor" : "none"} />
                        </button>
                    </div>
                    <p className="mt-5 text-xl font-semibold" data-testid="product-price">
                        {formatMAD(product.price)}
                    </p>

                    <p className="mt-8 text-sm text-white/70 leading-relaxed" data-testid="product-description">
                        {product.description}
                    </p>

                    {product.sizes?.length > 0 && (
                        <div className="mt-10">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold">
                                    {t("product.size")}
                                </p>
                                {size && (
                                    <p className="text-xs uppercase tracking-[0.2em] text-white/40">
                                        {t("product.selected")}: {size}
                                    </p>
                                )}
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                                {product.sizes.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setSize(s)}
                                        className={`h-12 border text-sm font-bold uppercase transition-colors ${
                                            size === s
                                                ? "bg-white text-black border-white"
                                                : "border-white/20 text-white hover:border-white"
                                        }`}
                                        data-testid={`size-${s}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={handleAdd}
                        className="mt-10 w-full h-14 bg-white text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-white/90 active:scale-[0.98] transition-all"
                        data-testid="add-to-cart-btn"
                    >
                        {t("product.add_to_cart")}
                    </button>

                    <div className="mt-10 pt-8 border-t border-white/10 space-y-4">
                        {[
                            { icon: Check, text: t("product.in_stock") },
                            { icon: Truck, text: t("product.delivery") },
                            { icon: ShieldCheck, text: t("product.cod") },
                            { icon: RotateCcw, text: t("product.returns") },
                        ].map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <div key={i} className="flex items-center gap-3 text-sm text-white/70">
                                    <Icon size={16} strokeWidth={1.5} className="text-white" />
                                    {f.text}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
